/**
 * Alinea texto live (Web Speech) con palabras + timestamps de Whisper.
 * Preferimos el léxico live; los tiempos salen de Whisper (o interpolación).
 */

/**
 * @param {string} s
 * @returns {string}
 */
export function normalizeToken(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9áéíóúüñ]+/gi, '')
    .replace(/[áéíóúüñ]/g, (ch) => {
      const map = { á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u', ü: 'u', ñ: 'n' }
      return map[ch] || ch
    })
}

/**
 * @param {string} text
 * @returns {string[]}
 */
export function tokenizeLive(text) {
  const raw = String(text || '').trim()
  if (!raw) return []
  return raw
    .split(/\s+/)
    .map((t) => t.replace(/^[^0-9A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+|[^0-9A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+$/g, ''))
    .filter(Boolean)
}

/**
 * Distancia de edición acotada (rápida) para tokens cortos.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function editDistance(a, b) {
  if (a === b) return 0
  if (!a) return b.length
  if (!b) return a.length
  if (Math.abs(a.length - b.length) > 2) return 99
  const m = a.length
  const n = b.length
  const dp = new Array(n + 1)
  for (let j = 0; j <= n; j++) dp[j] = j
  for (let i = 1; i <= m; i++) {
    let prev = dp[0]
    dp[0] = i
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j]
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + cost)
      prev = tmp
    }
  }
  return dp[n]
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
function tokensSimilar(a, b) {
  if (!a || !b) return false
  if (a === b) return true
  if (a.length >= 3 && b.length >= 3 && (a.includes(b) || b.includes(a))) {
    return Math.abs(a.length - b.length) <= 2
  }
  const maxLen = Math.max(a.length, b.length)
  if (maxLen <= 2) return a === b
  return editDistance(a, b) <= (maxLen <= 5 ? 1 : 2)
}

/**
 * Needleman–Wunsch simplificado: live (filas) vs whisper (columnas).
 * @param {string[]} liveToks
 * @param {{ word: string, start: number, end: number }[]} wWords
 * @returns {{ kind: 'match'|'live'|'whisper', live?: string, whisper?: object }[]}
 */
function alignSequences(liveToks, wWords) {
  const n = liveToks.length
  const m = wWords.length
  const MATCH = 3
  const MISMATCH = -2
  const GAP = -1

  /** @type {number[][]} */
  const score = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))
  /** @type {number[][]} 0=diag 1=up(live gap/whisper skip) 2=left(whisper gap/live insert) */
  const ptr = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))

  for (let i = 1; i <= n; i++) {
    score[i][0] = i * GAP
    ptr[i][0] = 1
  }
  for (let j = 1; j <= m; j++) {
    score[0][j] = j * GAP
    ptr[0][j] = 2
  }

  for (let i = 1; i <= n; i++) {
    const ln = normalizeToken(liveToks[i - 1])
    for (let j = 1; j <= m; j++) {
      const wn = normalizeToken(wWords[j - 1].word)
      const sim = tokensSimilar(ln, wn)
      const diag = score[i - 1][j - 1] + (sim ? MATCH : MISMATCH)
      const up = score[i - 1][j] + GAP
      const left = score[i][j - 1] + GAP
      if (diag >= up && diag >= left) {
        score[i][j] = diag
        ptr[i][j] = 0
      } else if (up >= left) {
        score[i][j] = up
        ptr[i][j] = 1
      } else {
        score[i][j] = left
        ptr[i][j] = 2
      }
    }
  }

  /** @type {{ kind: 'match'|'live'|'whisper', live?: string, whisper?: object }[]} */
  const path = []
  let i = n
  let j = m
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && ptr[i][j] === 0) {
      path.push({
        kind: 'match',
        live: liveToks[i - 1],
        whisper: wWords[j - 1],
      })
      i -= 1
      j -= 1
    } else if (i > 0 && (j === 0 || ptr[i][j] === 1)) {
      path.push({ kind: 'live', live: liveToks[i - 1] })
      i -= 1
    } else {
      path.push({ kind: 'whisper', whisper: wWords[j - 1] })
      j -= 1
    }
  }
  path.reverse()
  return path
}

/**
 * @param {{ kind: string, live?: string, whisper?: object }[]} path
 * @returns {{ word: string, start: number, end: number, from: 'live'|'whisper'|'hybrid' }[]}
 */
function pathToWords(path) {
  /** @type {{ word: string, start: number, end: number, from: string, pending?: boolean }[]} */
  const out = []
  let lastEnd = 0

  for (let k = 0; k < path.length; k++) {
    const step = path[k]
    if (step.kind === 'whisper') {
      // Omisiones de Whisper respecto al live: no aportan texto final
      if (step.whisper && Number.isFinite(step.whisper.end)) {
        lastEnd = Math.max(lastEnd, Number(step.whisper.end) || 0)
      }
      continue
    }
    if (step.kind === 'match' && step.whisper) {
      const start = Number(step.whisper.start)
      const end = Number(step.whisper.end)
      const s = Number.isFinite(start) ? start : lastEnd
      const e = Number.isFinite(end) && end > s ? end : s + 0.12
      out.push({
        word: step.live || step.whisper.word,
        start: s,
        end: e,
        from: 'hybrid',
      })
      lastEnd = e
      continue
    }
    // live-only: interpolar después, marcando pendiente
    out.push({
      word: step.live || '',
      start: lastEnd,
      end: lastEnd,
      from: 'live',
      pending: true,
    })
  }

  // Rellenar pendientes entre anclas con tiempo conocido
  for (let i = 0; i < out.length; i++) {
    if (!out[i].pending) continue
    let left = i - 1
    while (left >= 0 && out[left].pending) left -= 1
    let right = i + 1
    while (right < out.length && out[right].pending) right += 1
    const t0 = left >= 0 ? out[left].end : 0
    const t1 =
      right < out.length && !out[right].pending
        ? out[right].start
        : t0 + 0.18 * (right - i + 1)
    const runStart = left + 1
    const runEnd = right - 1
    const count = runEnd - runStart + 1
    const span = Math.max(0.08 * count, t1 - t0)
    for (let j = 0; j < count; j++) {
      const s = t0 + (span * j) / count
      const e = t0 + (span * (j + 1)) / count
      out[runStart + j].start = s
      out[runStart + j].end = e
      out[runStart + j].pending = false
    }
  }

  return out.map(({ word, start, end, from }) => ({
    word,
    start: Math.round(start * 1000) / 1000,
    end: Math.round(end * 1000) / 1000,
    from,
  }))
}

/**
 * Construye transcript final hybrid (o fallback a whisper / live solo).
 *
 * @param {string} liveText
 * @param {{
 *   text?: string,
 *   words?: { word: string, start: number, end: number }[],
 *   modelId?: string|null,
 *   modelTier?: string|null,
 *   source?: string,
 * }|null} whisper
 * @returns {{
 *   text: string,
 *   words: object[],
 *   source: 'hybrid'|'local_whisper'|'live_speech',
 *   liveText?: string,
 *   whisperText?: string,
 *   modelId?: string|null,
 *   modelTier?: string|null,
 * }}
 */
export function buildHybridTranscript(liveText, whisper) {
  const live = String(liveText || '').trim()
  const wText = whisper && whisper.text ? String(whisper.text).trim() : ''
  const wWords = Array.isArray(whisper && whisper.words)
    ? whisper.words
        .map((w) => ({
          word: String((w && (w.word || w.text)) || '').trim(),
          start: Number(w && w.start),
          end: Number(w && w.end),
        }))
        .filter((w) => w.word)
    : []

  const baseMeta = {
    liveText: live || undefined,
    whisperText: wText || undefined,
    modelId: (whisper && whisper.modelId) || null,
    modelTier: (whisper && whisper.modelTier) || null,
  }

  if (!live && !wText) {
    return { text: '', words: [], source: 'local_whisper', ...baseMeta }
  }

  if (!live) {
    return {
      text: wText,
      words: wWords,
      source: 'local_whisper',
      ...baseMeta,
    }
  }

  if (!wText && !wWords.length) {
    return {
      text: live,
      words: [],
      source: 'live_speech',
      ...baseMeta,
    }
  }

  const liveToks = tokenizeLive(live)
  if (!liveToks.length) {
    return {
      text: wText || live,
      words: wWords,
      source: wText ? 'local_whisper' : 'live_speech',
      ...baseMeta,
    }
  }

  if (!wWords.length) {
    // Sin timestamps: texto live + opcionalmente no hay words
    return {
      text: live,
      words: [],
      source: 'hybrid',
      ...baseMeta,
    }
  }

  const path = alignSequences(liveToks, wWords)
  const words = pathToWords(path)
  const text = words.map((w) => w.word).join(' ').trim() || live

  return {
    text,
    words,
    source: 'hybrid',
    ...baseMeta,
  }
}

export default buildHybridTranscript
